import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { SceneKeys, Palette } from '@core/config/constants';
import { findCharacter } from '@core/config/characters';
import { SHOP_ITEMS } from '@core/config/shop';
import { TextButton } from '@ui/components/TextButton';
import { CycleSelector } from '@ui/components/CycleSelector';
import { DressUpConfig, DRESS_COLORS, BACKGROUND_COLORS } from '@modules/dressup/config';

interface Point {
  readonly x: number;
  readonly y: number;
}

/** Una opcion de accesorio en el probador (proviene de los comprados en la tienda). */
interface AccessoryChoice {
  readonly id: string;
  readonly name: string;
  readonly emoji: string;
}

/**
 * Probador de estilo: viste a TU personaje elegido con los accesorios que compraste en
 * la tienda, un brillo/aura de color y un fondo. Al guardar el look, el accesorio queda
 * equipado de verdad (se ve en el menu) y ganás estrellas la primera vez en la visita.
 */
export class DressUpScene extends BaseScene {
  private glowIndex = 0;
  private accessoryIndex = 0;
  private backgroundIndex = 0;
  private rewarded = false;

  private accessoryOptions: AccessoryChoice[] = [];

  private backgroundRect!: Phaser.GameObjects.Rectangle;
  private glow!: Phaser.GameObjects.Ellipse;
  private accessoryText!: Phaser.GameObjects.Text;
  private coinsText!: Phaser.GameObjects.Text;

  private characterX = 0;
  private characterY = 0;
  private avatarHeight = 300;

  public constructor() {
    super(DressUpConfig.key);
  }

  public create(): void {
    this.initResponsive();
    this.rewarded = false;

    const { width, height } = this.scale;
    this.backgroundRect = this.add.rectangle(
      this.centerX,
      this.centerY,
      width,
      height,
      BACKGROUND_COLORS[this.backgroundIndex].value,
    );
    this.addTwinkles(20);

    this.add
      .text(this.centerX, height * 0.06, DressUpConfig.title, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '40px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.coinsText = this.add
      .text(width - 28, 24, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '30px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);
    this.refreshCoins();

    const character = findCharacter(this.profile.character);
    if (character === undefined) {
      this.renderNoCharacter();
      return;
    }

    this.buildAccessoryOptions();
    this.buildLayout(character.texture);
    this.redrawCharacter();
  }

  /** Arma la lista de accesorios: "Ninguno" + los comprados en la tienda. */
  private buildAccessoryOptions(): void {
    const owned: AccessoryChoice[] = SHOP_ITEMS.filter((item) => this.profile.ownsItem(item.id)).map((item) => ({
      id: item.id,
      name: item.name,
      emoji: item.emoji,
    }));
    this.accessoryOptions = [{ id: '', name: 'Ninguno', emoji: '' }, ...owned];

    const equippedIndex = this.accessoryOptions.findIndex((option) => option.id === this.profile.accessory);
    this.accessoryIndex = equippedIndex >= 0 ? equippedIndex : 0;
  }

  private buildLayout(texture: string): void {
    const { width, height } = this.scale;

    if (this.isPortrait) {
      this.characterX = this.centerX;
      this.characterY = height * 0.3;
      this.avatarHeight = Math.min(300, height * 0.3);
      const selectorWidth = Math.min(380, width * 0.82);
      const selectorYs = [height * 0.52, height * 0.65, height * 0.78];

      this.createCharacter(texture);
      this.createSelectors(this.centerX, selectorYs, selectorWidth);
      this.createButtons({ x: this.centerX, y: height - 150 }, { x: this.centerX, y: height - 74 });
      this.maybeShowStoreHint(this.centerX, selectorYs[0] - 70);
      return;
    }

    this.characterX = width * 0.3;
    this.characterY = height * 0.52;
    this.avatarHeight = Math.min(380, height * 0.58);
    const selectorWidth = Math.min(360, width * 0.4);
    const selectorX = Math.min(width * 0.72, width - selectorWidth / 2 - 24);
    const selectorYs = [height * 0.3, height * 0.52, height * 0.74];

    this.createCharacter(texture);
    this.createSelectors(selectorX, selectorYs, selectorWidth);
    this.createButtons({ x: this.characterX, y: height - 80 }, { x: selectorX, y: height - 80 });
    this.maybeShowStoreHint(selectorX, selectorYs[0] - 74);
  }

  private createCharacter(texture: string): void {
    this.glow = this.add.ellipse(
      this.characterX,
      this.characterY + 10,
      this.avatarHeight * 0.95,
      this.avatarHeight * 1.2,
      DRESS_COLORS[this.glowIndex].value,
      0.35,
    );
    this.tweens.add({
      targets: this.glow,
      scale: 1.06,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const figure = this.add.container(this.characterX, this.characterY);

    const avatar = this.add.image(0, 0, texture).setOrigin(0.5);
    avatar.setScale(this.avatarHeight / avatar.height);
    figure.add(avatar);

    this.accessoryText = this.add
      .text(0, -this.avatarHeight * 0.42, '', { fontSize: `${Math.round(this.avatarHeight * 0.3)}px` })
      .setOrigin(0.5);
    figure.add(this.accessoryText);

    this.tweens.add({
      targets: figure,
      y: figure.y - 10,
      duration: 640,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private createSelectors(selectorX: number, ys: number[], width: number): void {
    new CycleSelector(this, {
      x: selectorX,
      y: ys[0],
      width,
      title: 'Accesorio',
      labels: this.accessoryOptions.map((option) => option.name),
      startIndex: this.accessoryIndex,
      onChange: (index: number) => {
        this.accessoryIndex = index;
        this.redrawCharacter();
      },
    });

    new CycleSelector(this, {
      x: selectorX,
      y: ys[1],
      width,
      title: 'Brillo',
      labels: DRESS_COLORS.map((color) => color.name),
      startIndex: this.glowIndex,
      onChange: (index: number) => {
        this.glowIndex = index;
        this.redrawCharacter();
      },
    });

    new CycleSelector(this, {
      x: selectorX,
      y: ys[2],
      width,
      title: 'Fondo',
      labels: BACKGROUND_COLORS.map((color) => color.name),
      startIndex: this.backgroundIndex,
      onChange: (index: number) => {
        this.backgroundIndex = index;
        this.redrawCharacter();
      },
    });
  }

  private maybeShowStoreHint(x: number, y: number): void {
    if (this.accessoryOptions.length > 1) {
      return;
    }
    this.add
      .text(x, y, 'Comprá accesorios en la tienda 🛍️', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '18px',
        color: '#ffd54f',
        align: 'center',
        wordWrap: { width: 360 },
      })
      .setOrigin(0.5);
  }

  private createButtons(save: Point, back: Point): void {
    new TextButton(this, {
      x: save.x,
      y: save.y,
      label: 'Guardar look ✨',
      color: Palette.primary,
      onClick: () => this.saveLook(),
    });

    new TextButton(this, {
      x: back.x,
      y: back.y,
      label: 'Volver al menú',
      color: Palette.accent,
      onClick: () => this.scene.start(SceneKeys.Hub),
    });
  }

  private redrawCharacter(): void {
    this.backgroundRect.setFillStyle(BACKGROUND_COLORS[this.backgroundIndex].value);
    this.glow.setFillStyle(DRESS_COLORS[this.glowIndex].value, 0.35);
    this.accessoryText.setText(this.accessoryOptions[this.accessoryIndex].emoji);
  }

  private renderNoCharacter(): void {
    this.add
      .text(this.centerX, this.centerY - 60, 'Primero elegí tu personaje ✨', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '28px',
        color: Palette.textLight,
        align: 'center',
        wordWrap: { width: this.scale.width * 0.9 },
      })
      .setOrigin(0.5);

    new TextButton(this, {
      x: this.centerX,
      y: this.centerY + 30,
      label: '★ Elegir personaje',
      color: Palette.secondary,
      onClick: () => this.scene.start(SceneKeys.CharacterSelect),
    });

    new TextButton(this, {
      x: this.centerX,
      y: this.centerY + 120,
      label: 'Volver al menú',
      color: Palette.accent,
      onClick: () => this.scene.start(SceneKeys.Hub),
    });
  }

  private saveLook(): void {
    const choice = this.accessoryOptions[this.accessoryIndex];
    this.profile.setAccessory(choice.id);

    if (this.rewarded) {
      this.showSavedFeedback('¡Look actualizado! ✨');
      this.audio.playClick();
      return;
    }

    this.rewarded = true;
    this.profile.addCoins(DressUpConfig.coinsPerLook);
    this.audio.playReward();
    this.addConfetti(30);
    this.refreshCoins();
    this.showSavedFeedback(`¡Look guardado! +${DressUpConfig.coinsPerLook} ⭐`);
  }

  private showSavedFeedback(text: string): void {
    const message = this.add
      .text(this.centerX, this.scale.height * 0.16, text, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '30px',
        color: '#ffffff',
        backgroundColor: '#00000055',
        padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5);

    this.tweens.add({
      targets: message,
      y: message.y - 60,
      alpha: 0,
      duration: 1200,
      ease: 'Cubic.easeOut',
      onComplete: () => message.destroy(),
    });
  }

  private refreshCoins(): void {
    this.coinsText.setText(`⭐ ${this.profile.coins}`);
  }
}
